import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { AnalyticsEvents } from '@/services/analytics/events';
import { useTrialUpsellStore } from '@/stores/useTrialUpsellStore';
import { nextTick, ref, shallowRef } from 'vue';
import { useFirstEntrySuccessActions, type IFirstEntrySuccessConfig } from './useFirstEntrySuccessActions';
import { useFirstEntrySuccessDialog } from './useFirstEntrySuccessDialog';
import { useGrowthCta } from './useGrowthCta';
import { useOwnerActivationChannel } from './useOwnerActivationFlow';

const PAUSE_BEFORE_SUCCESS_DIALOG_MS = 700;

export const GrowthEntryTriggerDialog = {
  QuickEntry: 'quick_entry',
} as const;

type TGrowthEntryTriggerDialog = (typeof GrowthEntryTriggerDialog)[keyof typeof GrowthEntryTriggerDialog];

interface IGrowthEntryPostCreationParams {
  allowTrialUpsellAfterEntry?: boolean;
  requestTrialUpsellEligibilityCheck?: boolean;
  schoolClassId: number;
  wasFirstEntry: boolean;
}

interface IGrowthEntryPostCreationFlowOptions {
  onCreateAnotherEntry: () => void;
  triggerDialog: TGrowthEntryTriggerDialog;
}

export const useGrowthEntryPostCreationFlow = (options: IGrowthEntryPostCreationFlowOptions) => {
  const { trackEvent } = useAnalytics();
  const { popup, refresh, refreshSilently } = useGrowthCta();
  const { getFirstEntrySuccessConfig } = useFirstEntrySuccessActions();
  const firstEntrySuccessDialog = useFirstEntrySuccessDialog();
  const { isActivationActive, onSuccessSecondary } = useOwnerActivationChannel();
  const trialUpsellStore = useTrialUpsellStore();

  const firstEntryHandledInSession = ref(false);
  const pendingFirstEntrySuccess = ref(false);
  const pendingSuccessConfig = shallowRef<IFirstEntrySuccessConfig | null>(null);
  const ownerSecondEntryRequested = ref(false);
  const afterSecondEntryAction = shallowRef<(() => Promise<void> | void) | undefined>(undefined);

  const captureWasFirstEntry = (): boolean => popup.value === 'POPUP_FIRST_ENTRY' && !firstEntryHandledInSession.value;

  const handleEntryCreated = (params: IGrowthEntryPostCreationParams): void => {
    const wasFirstEntry = params.wasFirstEntry;
    pendingFirstEntrySuccess.value = wasFirstEntry;

    if (wasFirstEntry || params.allowTrialUpsellAfterEntry) {
      trialUpsellStore.recordFirstValueSuccess();
    }
    if (params.allowTrialUpsellAfterEntry) {
      trialUpsellStore.armPostEntryGrowthUpsellBypass();
    }
    if (params.requestTrialUpsellEligibilityCheck) {
      trialUpsellStore.requestEligibilityCheck();
    }

    if (wasFirstEntry) {
      firstEntryHandledInSession.value = true;
      trackEvent(AnalyticsEvents.growth.firstEntryCompleted, {
        trigger_dialog: options.triggerDialog,
      });
      pendingSuccessConfig.value = getFirstEntrySuccessConfig(params.schoolClassId);
      return;
    }

    pendingSuccessConfig.value = null;
    if (isActivationActive.value && onSuccessSecondary.value) {
      afterSecondEntryAction.value = onSuccessSecondary.value;
    }
  };

  const handleDetailsClose = async (): Promise<void> => {
    if (afterSecondEntryAction.value) {
      const action = afterSecondEntryAction.value;
      afterSecondEntryAction.value = undefined;
      await action();
      return;
    }
    if (!pendingFirstEntrySuccess.value || !pendingSuccessConfig.value) return;

    pendingFirstEntrySuccess.value = false;
    const config = pendingSuccessConfig.value;
    pendingSuccessConfig.value = null;
    const isOwnerActivation = config.isOwnerActivation ?? false;
    const ownerPrimary = isOwnerActivation
      ? () => {
          ownerSecondEntryRequested.value = true;
        }
      : config.onPrimary;

    await new Promise((resolve) => setTimeout(resolve, PAUSE_BEFORE_SUCCESS_DIALOG_MS));
    await firstEntrySuccessDialog.show({
      ...config,
      onPrimary: ownerPrimary,
      onClose: () => {
        if (isOwnerActivation && ownerSecondEntryRequested.value) {
          ownerSecondEntryRequested.value = false;
          afterSecondEntryAction.value = config.onSecondary;
          void refreshSilently();
          void nextTick(options.onCreateAnotherEntry);
          return;
        }
        if (isOwnerActivation) {
          void refresh();
          return;
        }
        void refreshSilently();
      },
    });
  };

  return { captureWasFirstEntry, handleDetailsClose, handleEntryCreated };
};
